import { useCallback, useEffect, useRef, useState } from 'react';
import type { OpsBackend } from '../backend/types.ts';
import { navigate } from '../lib.ts';
import { Icon, type IconName } from './Icon.tsx';
import { teamClick } from './Team.tsx';

/**
 * Bell in the top bar (API mode): approvals waiting for you and decisions on yours, team messages /
 * mentions / tasks, system alerts. The server keeps them per user; this polls the unread count every 15 s
 * and loads the list when opened. Opening an item marks it read and goes to its page.
 */
type Category = 'APPROVAL' | 'TEAM' | 'SYSTEM';
interface Item {
  id: number;
  category: Category;
  severity: 'INFO' | 'SUCCESS' | 'WARNING' | 'CRITICAL';
  title: string;
  body: string | null;
  link: string | null;
  count: number;
  createdAt: string;
  read: boolean;
}
const TABS: { id: Category | 'ALL'; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'APPROVAL', label: 'Approvals' },
  { id: 'TEAM', label: 'Team' },
  { id: 'SYSTEM', label: 'System' },
];
const CAT_ICON: Record<Category, IconName> = { APPROVAL: 'audit', TEAM: 'chat', SYSTEM: 'alert' };

function ago(iso: string, now: number): string {
  const s = Math.max(0, (now - Date.parse(iso)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86_400)} d ago`;
}

export function Notifications({ server }: { server: NonNullable<OpsBackend['server']> }) {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Category | 'ALL'>('ALL');
  const [items, setItems] = useState<Item[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const latest = useRef(-1);
  const box = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const r = await server.get<{ unread: number; items: Item[] }>('/v1/admin/notifications?limit=60');
    if (r.ok) {
      setItems(r.value.items);
      setUnread(r.value.unread);
      setErr(null);
    } else setErr(r.error);
    setNow(Date.now());
  }, [server]);

  const poll = useCallback(async () => {
    const r = await server.get<{ unread: number; latestId: number }>('/v1/admin/notifications/unread');
    if (!r.ok) return;
    // A sound only for something new (not on the first poll after sign-in).
    if (latest.current >= 0 && r.value.latestId > latest.current) teamClick();
    latest.current = r.value.latestId;
    setUnread(r.value.unread);
  }, [server]);

  useEffect(() => {
    const t0 = setTimeout(() => void poll(), 0);
    const t = setInterval(() => void poll(), 15_000);
    return () => {
      clearTimeout(t0);
      clearInterval(t);
    };
  }, [poll]);

  useEffect(() => {
    if (!open) return;
    const t0 = setTimeout(() => void load(), 0);
    const t = setInterval(() => void load(), 15_000);
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    addEventListener('mousedown', onDown);
    addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t0);
      clearInterval(t);
      removeEventListener('mousedown', onDown);
      removeEventListener('keydown', onKey);
    };
  }, [open, load]);

  const markRead = async (body: Record<string, unknown>) => {
    await server.post('/v1/admin/notifications/read', body);
    await load();
  };
  const openItem = (n: Item) => {
    if (!n.read) void markRead({ ids: [n.id] });
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const shown = (items ?? []).filter((n) => tab === 'ALL' || n.category === tab);
  const unreadIn = (c: Category | 'ALL') => (items ?? []).filter((n) => !n.read && (c === 'ALL' || n.category === c)).length;
  return (
    <div className="nt" ref={box}>
      <button type="button" className={`icon-btn nt-bell ${unread ? 'has' : ''}`} aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`} aria-expanded={open} aria-haspopup="dialog" title="Notifications" onClick={() => setOpen(!open)} data-testid="notifications">
        <Icon name="bell" size={17} />
        {unread > 0 && (
          <span className="nt-count" data-testid="notifications-unread">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="nt-panel" role="dialog" aria-label="Notifications" data-testid="notifications-panel">
          <div className="nt-head">
            <strong>Notifications</strong>
            <button type="button" className="btn ghost nt-all" disabled={!unread} onClick={() => void markRead({ all: true })}>
              Mark all read
            </button>
          </div>
          <div className="nt-tabs" role="tablist">
            {TABS.map((t) => (
              <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={tab === t.id ? 'on' : ''} onClick={() => setTab(t.id)}>
                {t.label}
                {unreadIn(t.id) > 0 && <span className="nt-pill">{unreadIn(t.id)}</span>}
              </button>
            ))}
          </div>
          {err && (
            <p role="alert" className="field-error nt-empty">
              {err}
            </p>
          )}
          {items === null && !err ? (
            <p className="nt-empty" aria-busy="true">
              Loading…
            </p>
          ) : shown.length === 0 ? (
            <p className="nt-empty">{tab === 'APPROVAL' ? 'No approvals for you.' : tab === 'TEAM' ? 'No team messages.' : tab === 'SYSTEM' ? 'No system alerts — everything measured is working.' : 'Nothing yet.'}</p>
          ) : (
            <ul className="nt-list">
              {shown.map((n) => (
                <li key={n.id}>
                  <button type="button" className={`nt-item sev-${n.severity.toLowerCase()} ${n.read ? 'read' : 'unread'}`} onClick={() => openItem(n)} data-testid="notification-item">
                    <span className="nt-ico" aria-hidden>
                      <Icon name={CAT_ICON[n.category]} size={15} />
                    </span>
                    <span className="nt-text">
                      <span className="nt-title">
                        {n.title}
                        {n.count > 1 && <span className="nt-times"> ×{n.count}</span>}
                      </span>
                      {n.body && <span className="nt-body">{n.body}</span>}
                      <span className="nt-meta">
                        {ago(n.createdAt, now)}
                        {n.link ? ' · open →' : ''}
                      </span>
                    </span>
                    {!n.read && <span className="nt-dot" aria-label="unread" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
